import { useSyncExternalStore } from "react";

/**
 * Guest access rules for the Learning Hub and Self-Assessments.
 *
 * Signed-out visitors can browse the whole catalogue (categories, A–Z,
 * titles, icons, descriptions) plus a small set of genuinely complete
 * resources. Everything else stays visible but shows a "Members" lock.
 */

/** Catalogue / directory pages guests may always browse. */
const GUEST_BROWSE_PATHS = [
  "/learn",
  "/learn/assessments",
  "/learn/assessments/a-z",
  "/learn/therapies",
  "/learn/glossary",
];

/** Full Learning Hub resources open to guests (path prefixes). */
export const GUEST_FREE_LEARN_PREFIXES = [
  "/learn/window-of-tolerance",
  "/learn/triggers",
  // Articles are free to read; the check-in gates itself for members.
  "/learn/men-and-trauma",
];

/** Self-assessments guests can start, complete and see full results for. */
export const GUEST_FREE_ASSESSMENT_SLUGS = ["trauma-responses", "attachment"];

function normalise(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

/** True when a signed-out visitor may see the full page at this path. */
export function isGuestOpenPath(pathname: string): boolean {
  const path = normalise(pathname);
  if (GUEST_BROWSE_PATHS.includes(path)) return true;
  if (GUEST_FREE_LEARN_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) return true;
  if (
    GUEST_FREE_ASSESSMENT_SLUGS.some((slug) => path === `/learn/assessments/${slug}`)
  )
    return true;
  return false;
}

export function isGuestOpenAssessment(slug: string): boolean {
  return GUEST_FREE_ASSESSMENT_SLUGS.includes(slug);
}

/* ------------------------------------------------------------------ */
/* Shared "members only" modal store                                    */
/* ------------------------------------------------------------------ */

type LockState = { open: boolean; label: string | null };

let state: LockState = { open: false, label: null };
const listeners = new Set<() => void>();

function emit(next: LockState) {
  state = next;
  listeners.forEach((l) => l());
}

export function openGuestLock(label?: string) {
  emit({ open: true, label: label ?? null });
}

export function closeGuestLock() {
  emit({ open: false, label: null });
}

const SERVER_STATE: LockState = { open: false, label: null };

export function useGuestLockState(): LockState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
    () => SERVER_STATE,
  );
}
